import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  generateSingleBillReceiptHtml,
  generatePersonBackupReceiptHtml,
  generateFullBackupReceiptHtml,
  downloadReceiptPdf,
  printReceiptDirectly
} from '../utils/receiptPdfGenerator';
import DataMigrationModal from '../components/DataMigrationModal';
import { apiUrl } from '../api';

// Helper to clean & compress dataset to minimal KB
export const compressDataset = (backupDoc) => {
  const data = backupDoc.data || backupDoc;
  
  const cleanSales = (data.sales || []).map(s => ({
    id: s.id,
    date: s.date || '',
    invoiceNo: s.invoiceNo || s.vchNo || '',
    custName: s.custName || '',
    custMob: s.custMob || '',
    saleType: s.saleType || 'CASH',
    netAmount: parseFloat(s.netAmount || s.grossAmount || 0) || 0,
    items: (s.items || []).map(i => ({
      productName: i.productName || '',
      batch: i.batch || '',
      exp: i.exp || '',
      qty: parseInt(i.qty) || 1,
      rate: parseFloat(i.rate) || 0,
      mrp: parseFloat(i.mrp) || 0,
      amount: parseFloat(i.amount) || 0
    }))
  }));

  const cleanPurchases = (data.purchases || []).map(p => ({
    id: p.id,
    date: p.date || '',
    invoiceNo: p.invoiceNo || '',
    supplierName: p.supplierName || '',
    supplierGst: p.supplierGst || '',
    paymentType: p.paymentType || 'Credit',
    netAmount: parseFloat(p.netAmount || p.grossAmount || p.totalAmount || 0) || 0,
    items: (p.items || []).map(i => ({
      productName: i.productName || '',
      batch: i.batch || '',
      exp: i.exp || '',
      qty: parseInt(i.qty) || 1,
      rate: parseFloat(i.rate) || 0,
      mrp: parseFloat(i.mrp) || 0,
      amount: parseFloat(i.amount) || 0
    }))
  }));

  const cleanCredit = (data.credit || []).map(c => ({
    id: c.id,
    name: c.name || '',
    phone: c.phone || '',
    dueAmount: parseFloat(c.dueAmount || 0) || 0,
    lastUpdate: c.lastUpdate || ''
  }));

  const cleanStockists = (data.stockist || []).map(s => ({
    id: s.id,
    name: s.name || '',
    address: s.address || '',
    phone: s.phone || '',
    gst: s.gst || '',
    totalPurchases: parseFloat(s.totalPurchases || 0) || 0,
    totalBills: parseInt(s.totalBills || 0) || 0
  }));

  return {
    id: backupDoc.id,
    name: backupDoc.name,
    date: backupDoc.date,
    time: backupDoc.time,
    deviceInfo: backupDoc.deviceInfo,
    notes: backupDoc.notes,
    data: {
      sales: cleanSales,
      purchases: cleanPurchases,
      credit: cleanCredit,
      stockist: cleanStockists
    }
  };
};

// Backwards-compatible export that generates authentic Receipt Document
export const generateBillTypeHtmlDocument = (backupDoc) => {
  return generateFullBackupReceiptHtml(backupDoc);
};

const Backups = () => {
  // Navigation mode: 'system' (All Backup Snapshots) | 'person' (Specific Person Backup & Bills)
  const [activeMainTab, setActiveMainTab] = useState('system');

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedBackupForRestore, setSelectedBackupForRestore] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [backupNotes, setBackupNotes] = useState('');
  const [showNewBackupModal, setShowNewBackupModal] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  // Specific Person Feature States
  const [liveSales, setLiveSales] = useState([]);
  const [liveCredit, setLiveCredit] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null); // { name, phone, sales: [], credit: null, totalSpent: 0 }
  const [personSearchQuery, setPersonSearchQuery] = useState('');
  const [personExpandedBillId, setPersonExpandedBillId] = useState(null);

  // Backup creation modal: 'full' | 'person'
  const [backupType, setBackupType] = useState('full');
  const [selectedPersonForBackup, setSelectedPersonForBackup] = useState(null);

  // In-App Receipt Preview Modal
  const [previewReceiptHtml, setPreviewReceiptHtml] = useState(null);
  const [previewReceiptTitle, setPreviewReceiptTitle] = useState('');

  // Bill Viewer Modal States
  const [viewingBackupData, setViewingBackupData] = useState(null);
  const [billViewTab, setBillViewTab] = useState('sales'); // 'sales' | 'purchases' | 'credit' | 'stockists'
  const [billSearchQuery, setBillSearchQuery] = useState('');
  const [expandedBillId, setExpandedBillId] = useState(null);
  const [modalCustomerFilter, setModalCustomerFilter] = useState('');

  // File upload for restore
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    fetchAllData();
    // Auto-poll every 10 seconds for real-time sync across PCs
    const interval = setInterval(fetchAllData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    try {
      fetchBackups();
      fetchLiveSalesAndCredit();
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const res = await fetch(apiUrl('/api/backups'));
      if (res.ok) {
        const data = await res.json();
        setBackups(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching backups:', err);
    }
  };

  const fetchLiveSalesAndCredit = async () => {
    try {
      const [salesRes, creditRes] = await Promise.all([
        fetch(apiUrl('/api/sales')).catch(() => null),
        fetch(apiUrl('/api/credit')).catch(() => null)
      ]);

      if (salesRes && salesRes.ok) {
        const salesData = await salesRes.json();
        if (Array.isArray(salesData)) setLiveSales(salesData);
      }

      if (creditRes && creditRes.ok) {
        const creditData = await creditRes.json();
        if (Array.isArray(creditData)) setLiveCredit(creditData);
      }
    } catch (e) {
      console.warn('Silent fail loading live sales/credit:', e);
    }
  };

  // Aggregated Persons List from live sales, credit, and backup datasets
  const aggregatedPersons = useMemo(() => {
    const personMap = new Map();

    // 1. Live Sales
    (liveSales || []).forEach(s => {
      const name = (s.custName || '').trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!personMap.has(key)) {
        personMap.set(key, {
          name: name,
          phone: s.custMob || '',
          sales: [],
          credit: null,
          totalSpent: 0
        });
      }
      const p = personMap.get(key);
      if (!p.phone && s.custMob) p.phone = s.custMob;
      p.sales.push(s);
      p.totalSpent += parseFloat(s.netAmount || s.grossAmount || 0) || 0;
    });

    // 2. Credit accounts
    (liveCredit || []).forEach(c => {
      const name = (c.name || '').trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!personMap.has(key)) {
        personMap.set(key, {
          name: name,
          phone: c.phone || '',
          sales: [],
          credit: c,
          totalSpent: 0
        });
      } else {
        const p = personMap.get(key);
        p.credit = c;
        if (!p.phone && c.phone) p.phone = c.phone;
      }
    });

    // 3. Fallback: If live sales is empty, extract from latest backups
    if (personMap.size === 0 && backups.length > 0) {
      backups.forEach(b => {
        if (b.data?.sales) {
          b.data.sales.forEach(s => {
            const name = (s.custName || '').trim();
            if (!name) return;
            const key = name.toLowerCase();
            if (!personMap.has(key)) {
              personMap.set(key, {
                name: name,
                phone: s.custMob || '',
                sales: [],
                credit: null,
                totalSpent: 0
              });
            }
            const p = personMap.get(key);
            p.sales.push(s);
            p.totalSpent += parseFloat(s.netAmount || s.grossAmount || 0) || 0;
          });
        }
      });
    }

    return Array.from(personMap.values()).sort((a, b) => b.sales.length - a.sales.length || a.name.localeCompare(b.name));
  }, [liveSales, liveCredit, backups]);

  // Filtered persons based on search query in the Person Tab
  const filteredPersons = useMemo(() => {
    if (!personSearchQuery.trim()) return aggregatedPersons;
    const q = personSearchQuery.toLowerCase();
    return aggregatedPersons.filter(p => 
      p.name.toLowerCase().includes(q) ||
      (p.phone && p.phone.toLowerCase().includes(q))
    );
  }, [aggregatedPersons, personSearchQuery]);

  // Handle Create Backup (Full System OR Specific Person)
  const handleCreateBackup = async () => {
    try {
      setIsCreatingBackup(true);
      const userAgent = navigator.userAgent || 'Web Browser';
      const isWindows = userAgent.includes('Windows') ? 'Windows PC' : userAgent.includes('Mac') ? 'Mac' : 'Web Terminal';
      
      const isPersonMode = backupType === 'person' && selectedPersonForBackup;
      const personName = isPersonMode ? selectedPersonForBackup.name : null;

      const payload = {
        notes: backupNotes.trim() || (isPersonMode ? `Person Backup: ${personName}` : 'Manual Cloud Backup'),
        clientDevice: `${isWindows} (${window.location.hostname})`,
        specificPerson: personName
      };

      const res = await fetch(apiUrl('/api/backups'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.details || errJson.error || `Failed to create cloud backup (Status: ${res.status})`);
      }
      const data = await res.json();

      setShowNewBackupModal(false);
      setBackupNotes('');
      setSelectedPersonForBackup(null);
      setBackupType('full');
      await fetchBackups();

      setStatusMessage({
        type: 'success',
        text: isPersonMode 
          ? `Cloud backup created for ${personName}! Snapshot saved to Firestore.`
          : `Full cloud backup created successfully! Snapshot saved to Firestore.`
      });

      const dateStr = new Date().toISOString().split('T')[0];

      // Generate Authentic Medical Receipt PDF (safely, won't block cloud backup on browser popup issue)
      try {
        if (isPersonMode) {
          const receiptHtml = generatePersonBackupReceiptHtml(
            selectedPersonForBackup.name,
            selectedPersonForBackup.sales,
            selectedPersonForBackup.credit
          );
          const fileName = `Person_Backup_${personName.replace(/[^a-zA-Z0-9]/g, '_')}_${dateStr}.pdf`;
          await downloadReceiptPdf(receiptHtml, fileName);
        } else {
          const fullDoc = data.fullData || { data: { sales: liveSales, credit: liveCredit } };
          const receiptHtml = generateFullBackupReceiptHtml(fullDoc);
          const fileName = `Medical_Backup_Register_${dateStr}_${Date.now().toString().slice(-4)}.pdf`;
          await downloadReceiptPdf(receiptHtml, fileName);
        }
      } catch (pdfErr) {
        console.warn('PDF download warning:', pdfErr);
      }
    } catch (err) {
      console.error('Error taking backup:', err);
      setStatusMessage({ type: 'error', text: 'Backup creation failed: ' + err.message });
    } finally {
      setIsCreatingBackup(false);
    }
  };

  // Download Receipt PDF for any existing backup snapshot
  const handleDownloadReceiptPdf = async (backupId, backupName) => {
    try {
      const res = await fetch(apiUrl(`/api/backups/${backupId}`));
      if (!res.ok) throw new Error('Failed to fetch backup file');
      const data = await res.json();

      const receiptHtml = generateFullBackupReceiptHtml(data);
      const fileName = `${backupName || 'Medical_Backup_Register'}.pdf`;
      await downloadReceiptPdf(receiptHtml, fileName);

      setStatusMessage({
        type: 'success',
        text: `Receipt PDF downloaded successfully in pharmacy bill format!`
      });
    } catch (err) {
      alert('Error downloading receipt PDF: ' + err.message);
    }
  };

  // Directly print receipt for a backup snapshot
  const handlePrintBackupReceipt = async (backupId) => {
    try {
      const res = await fetch(apiUrl(`/api/backups/${backupId}`));
      if (!res.ok) throw new Error('Failed to fetch backup file');
      const data = await res.json();

      const receiptHtml = generateFullBackupReceiptHtml(data);
      printReceiptDirectly(receiptHtml);
    } catch (err) {
      alert('Error printing receipt: ' + err.message);
    }
  };

  // Download raw JSON (Secondary action for database developers / restore)
  const handleDownloadCompactJson = async (backupId, backupName) => {
    try {
      const res = await fetch(apiUrl(`/api/backups/${backupId}`));
      if (!res.ok) throw new Error('Failed to fetch backup file');
      const data = await res.json();

      const compact = compressDataset(data);
      const jsonText = JSON.stringify(compact);
      const sizeInKb = (new Blob([jsonText]).size / 1024).toFixed(1);

      const blob = new Blob([jsonText], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${backupName || 'MediFlow_Backup'}_raw.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatusMessage({
        type: 'success',
        text: `Database JSON snapshot downloaded (${sizeInKb} KB).`
      });
    } catch (err) {
      alert('Error downloading JSON: ' + err.message);
    }
  };

  const handleOpenBillViewer = async (backupItem) => {
    try {
      const res = await fetch(apiUrl(`/api/backups/${backupItem.id}`));
      if (!res.ok) throw new Error('Failed to load backup bills');
      const detail = await res.json();
      setViewingBackupData(detail);
      setBillViewTab('sales');
      setBillSearchQuery('');
      setModalCustomerFilter('');
      setExpandedBillId(null);
    } catch (err) {
      alert('Error loading backup bill details: ' + err.message);
    }
  };

  const handleConfirmRestore = async () => {
    if (!selectedBackupForRestore) return;
    try {
      setIsRestoring(true);
      const res = await fetch(apiUrl(`/api/backups/${selectedBackupForRestore.id}/restore`), {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to restore backup');
      const result = await res.json();
      
      setSelectedBackupForRestore(null);
      setStatusMessage({
        type: 'success',
        text: result.message || 'Database restored successfully from cloud backup!'
      });
      fetchLiveSalesAndCredit();
    } catch (err) {
      alert('Error restoring data: ' + err.message);
    } finally {
      setIsRestoring(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingFile(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          let jsonContent;
          const text = e.target.result;
          
          if (file.name.endsWith('.html') || text.includes('mediflow-backup-data')) {
            const match = text.match(/<script id="mediflow-backup-data" type="application\/json">\s*([\s\S]*?)\s*<\/script>/);
            if (match && match[1]) {
              jsonContent = JSON.parse(match[1]);
            } else {
              throw new Error("Could not find backup dataset in the HTML file.");
            }
          } else {
            jsonContent = JSON.parse(text);
          }

          const res = await fetch(apiUrl('/api/backups/restore-upload'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(jsonContent)
          });
          if (!res.ok) throw new Error('Failed to restore from file');
          setStatusMessage({
            type: 'success',
            text: 'Data restored successfully!'
          });
          if (fileInputRef.current) fileInputRef.current.value = '';
          fetchAllData();
        } catch (parseErr) {
          alert('Invalid backup file or restore failed: ' + parseErr.message);
        } finally {
          setUploadingFile(false);
        }
      };
      reader.readAsText(file);
    } catch (err) {
      setUploadingFile(false);
      alert('Error reading file: ' + err.message);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup snapshot?')) return;
    try {
      const res = await fetch(apiUrl(`/api/backups/${backupId}`), {
        method: 'DELETE'
      });
      if (res.ok) {
        setBackups(prev => prev.filter(b => b.id !== backupId));
        setStatusMessage({ type: 'success', text: 'Backup snapshot deleted successfully.' });
      }
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  // Specific Person Actions
  const handlePrintSingleBill = (bill) => {
    const html = generateSingleBillReceiptHtml(bill);
    printReceiptDirectly(html);
  };

  const handleDownloadSingleBillPdf = async (bill, custName) => {
    const html = generateSingleBillReceiptHtml(bill);
    const fileName = `Bill_${bill.invoiceNo || bill.vchNo || 'Receipt'}_${(custName || 'Customer').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
    await downloadReceiptPdf(html, fileName);
    setStatusMessage({
      type: 'success',
      text: `Bill receipt downloaded as PDF for ${custName || 'Customer'}!`
    });
  };

  const handleViewBillReceipt = (bill) => {
    const html = generateSingleBillReceiptHtml(bill);
    setPreviewReceiptHtml(html);
    setPreviewReceiptTitle(`Tax Invoice Receipt - ${bill.custName || 'Customer'}`);
  };

  const handlePrintPersonBackupAll = (person) => {
    const html = generatePersonBackupReceiptHtml(person.name, person.sales, person.credit);
    printReceiptDirectly(html);
  };

  const handleDownloadPersonBackupPdf = async (person) => {
    const html = generatePersonBackupReceiptHtml(person.name, person.sales, person.credit);
    const fileName = `Person_Backup_${person.name.replace(/[^a-zA-Z0-9]/g, '_')}_All_Bills.pdf`;
    await downloadReceiptPdf(html, fileName);
    setStatusMessage({
      type: 'success',
      text: `All bills for ${person.name} downloaded as PDF Receipt!`
    });
  };

  const handleCreatePersonCloudBackup = async (person) => {
    setSelectedPersonForBackup(person);
    setBackupType('person');
    setBackupNotes(`Dedicated Backup Snapshot for ${person.name}`);
    setShowNewBackupModal(true);
  };

  const filteredBackups = backups.filter(b => {
    const q = searchQuery.toLowerCase();
    return (
      (b.name && b.name.toLowerCase().includes(q)) ||
      (b.date && b.date.toLowerCase().includes(q)) ||
      (b.notes && b.notes.toLowerCase().includes(q)) ||
      (b.deviceInfo && b.deviceInfo.toLowerCase().includes(q))
    );
  });

  // Unique customers for the Explorer modal filter
  const modalCustomersList = useMemo(() => {
    if (!viewingBackupData?.data?.sales) return [];
    const set = new Set();
    viewingBackupData.data.sales.forEach(s => {
      if (s.custName) set.add(s.custName.trim());
    });
    return Array.from(set).sort();
  }, [viewingBackupData]);

  // Filtered bills inside the Viewer Modal
  const getFilteredBills = () => {
    if (!viewingBackupData?.data) return [];
    const q = billSearchQuery.toLowerCase();
    
    if (billViewTab === 'sales') {
      let list = viewingBackupData.data.sales || [];
      if (modalCustomerFilter) {
        list = list.filter(s => s.custName && s.custName.trim().toLowerCase() === modalCustomerFilter.toLowerCase());
      }
      if (!q) return list;
      return list.filter(sale => 
        (sale.custName && sale.custName.toLowerCase().includes(q)) ||
        (sale.invoiceNo && sale.invoiceNo.toLowerCase().includes(q)) ||
        (sale.vchNo && sale.vchNo.toLowerCase().includes(q)) ||
        (sale.items && sale.items.some(i => i.productName && i.productName.toLowerCase().includes(q)))
      );
    }

    if (billViewTab === 'purchases') {
      const list = viewingBackupData.data.purchases || [];
      if (!q) return list;
      return list.filter(pur => 
        (pur.supplierName && pur.supplierName.toLowerCase().includes(q)) ||
        (pur.invoiceNo && pur.invoiceNo.toLowerCase().includes(q)) ||
        (pur.items && pur.items.some(i => i.productName && i.productName.toLowerCase().includes(q)))
      );
    }

    if (billViewTab === 'credit') {
      const list = viewingBackupData.data.credit || [];
      if (!q) return list;
      return list.filter(c => 
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q))
      );
    }

    if (billViewTab === 'stockists') {
      const list = viewingBackupData.data.stockist || [];
      if (!q) return list;
      return list.filter(s => 
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.phone && s.phone.toLowerCase().includes(q)) ||
        (s.gst && s.gst.toLowerCase().includes(q))
      );
    }

    return [];
  };

  const currentBillsList = getFilteredBills();

  return (
    <div className="h-full overflow-y-auto bg-[#F8FAFC] p-3 sm:p-4 font-sans">
      <div className="max-w-7xl mx-auto space-y-4 pb-12">

        {/* Status Alert Banner */}
        {statusMessage && (
          <div className={`p-3 rounded-xl flex items-center justify-between shadow-md transition-all duration-300 animate-in fade-in slide-in-from-top-2 ${
            statusMessage.type === 'success' 
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-emerald-500/20' 
              : 'bg-gradient-to-r from-rose-600 to-red-600 text-white shadow-rose-500/20'
          }`}>
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center font-bold text-xs shrink-0">
                {statusMessage.type === 'success' ? '✓' : '!'}
              </div>
              <p className="text-xs font-bold tracking-tight">{statusMessage.text}</p>
            </div>
            <button 
              onClick={() => setStatusMessage(null)}
              className="w-6 h-6 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white font-bold text-sm transition-colors"
            >
              ×
            </button>
          </div>
        )}

        {/* Sleek, Modern Hero Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-4 sm:p-5 text-white shadow-lg border border-slate-700/60 relative overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
            {/* Title & Subtitle */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0 shadow-inner">
                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold tracking-tight text-white flex items-center gap-2">
                  Cloud Backups & Registers
                </h1>
                <p className="text-xs text-slate-400 font-medium">
                  Pharmacy database, customer receipts & data sync
                </p>
              </div>
            </div>

            {/* Clean Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Create Backup */}
              <button
                onClick={() => {
                  setBackupType('full');
                  setSelectedPersonForBackup(null);
                  setShowNewBackupModal(true);
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs shadow-md shadow-emerald-500/20 active:scale-95 transition-all"
              >
                <svg className="w-4 h-4 stroke-[2.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <span>New Backup</span>
              </button>

              {/* Import Excel / CSV */}
              <button
                onClick={() => setShowMigrationModal(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-indigo-600/30 hover:bg-indigo-600/40 text-indigo-200 hover:text-white border border-indigo-500/30 font-semibold text-xs active:scale-95 transition-all"
                title="Import data from Excel or CSV file"
              >
                <svg className="w-4 h-4 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Import Data</span>
              </button>

              {/* Restore */}
              <input
                type="file"
                ref={fileInputRef}
                accept=".html,.json"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold text-xs active:scale-95 transition-all disabled:opacity-50"
                title="Restore from JSON / HTML backup file"
              >
                {uploadingFile ? (
                  <svg className="w-4 h-4 animate-spin text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                )}
                <span>Restore</span>
              </button>

              {/* Refresh */}
              <button
                onClick={fetchAllData}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white border border-slate-700 transition-all active:scale-95"
                title="Refresh list"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mode Tabs */}
        <div className="flex items-center justify-between gap-2 bg-white p-1.5 rounded-2xl border border-slate-200/90 shadow-sm">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setActiveMainTab('system')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeMainTab === 'system'
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <span>🗄️ Full Backups</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                activeMainTab === 'system' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
              }`}>
                {backups.length}
              </span>
            </button>

            <button
              onClick={() => setActiveMainTab('person')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeMainTab === 'person'
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-blue-50'
              }`}
            >
              <span>👤 Person-wise Bills</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                activeMainTab === 'person' ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'
              }`}>
                {aggregatedPersons.length}
              </span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* VIEW 1: SPECIFIC PERSON BACKUP & SEPARATE BILLS          */}
        {/* ======================================================== */}
        {activeMainTab === 'person' && (
          <div className="space-y-4">
            {/* Person Search & Selector Card */}
            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                    <span>👤</span>
                    <span>Select or Search Specific Person (व्यक्ती / ग्राहक निवडा)</span>
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    Select a customer to view their separate individual bills, print receipts, and take person-specific backup.
                  </p>
                </div>

                {/* Search Input */}
                <div className="relative w-full sm:w-80">
                  <input
                    type="text"
                    placeholder="Search customer by name or phone..."
                    value={personSearchQuery}
                    onChange={(e) => setPersonSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                  <svg className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Customer Chips / Quick Selection Grid */}
              <div className="flex flex-wrap gap-2 pt-1 max-h-48 overflow-y-auto pr-1">
                {filteredPersons.length === 0 ? (
                  <p className="text-xs font-bold text-slate-400 py-2">No customers found matching "{personSearchQuery}".</p>
                ) : (
                  filteredPersons.map((p, idx) => {
                    const isSelected = selectedPerson?.name.toLowerCase() === p.name.toLowerCase();
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          setSelectedPerson(p);
                          setPersonExpandedBillId(null);
                        }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 border ${
                          isSelected
                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-500/30 scale-[1.02]'
                            : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                      >
                        <span>{p.name}</span>
                        <span className={`px-1.5 py-0.2 rounded-md text-[9px] font-black ${
                          isSelected ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {p.sales.length} Bills
                        </span>
                        {p.credit && p.credit.dueAmount > 0 && (
                          <span className={`px-1.5 py-0.2 rounded-md text-[9px] font-black ${
                            isSelected ? 'bg-rose-500 text-white' : 'bg-rose-100 text-rose-700'
                          }`}>
                            Due: ₹{parseFloat(p.credit.dueAmount).toFixed(0)}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Selected Person Profile & Separate Bills View */}
            {selectedPerson ? (
              <div className="space-y-4 animate-in fade-in duration-200">
                {/* Person Header & Action Bar */}
                <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-5 sm:p-6 rounded-2xl shadow-xl border border-slate-800 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/40 text-[10px] font-black uppercase tracking-wider">
                        Specific Person Selected
                      </span>
                      {selectedPerson.phone && (
                        <span className="text-xs text-slate-300 font-semibold">
                          📞 {selectedPerson.phone}
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl sm:text-2xl font-black text-white mt-1 uppercase tracking-tight">
                      {selectedPerson.name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-xs font-semibold text-slate-300">
                      <div>Total Invoices: <strong className="text-white font-black">{selectedPerson.sales.length} Bills</strong></div>
                      <div>• Total Purchases: <strong className="text-emerald-400 font-black">₹{selectedPerson.totalSpent.toFixed(2)}</strong></div>
                      {selectedPerson.credit && selectedPerson.credit.dueAmount > 0 && (
                        <div>• Udhari / Due Balance: <strong className="text-rose-400 font-black">₹{parseFloat(selectedPerson.credit.dueAmount).toFixed(2)}</strong></div>
                      )}
                    </div>
                  </div>

                  {/* Primary Actions for This Specific Person */}
                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* Download PDF Receipt */}
                    <button
                      onClick={() => handleDownloadPersonBackupPdf(selectedPerson)}
                      className="px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs transition-all shadow-md flex items-center gap-2 hover:scale-105 active:scale-95"
                      title="Download complete multi-bill statement in receipt PDF format"
                    >
                      <svg className="w-4 h-4 stroke-[2.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download All Bills (PDF Receipt)</span>
                    </button>

                    {/* Print All Bills */}
                    <button
                      onClick={() => handlePrintPersonBackupAll(selectedPerson)}
                      className="px-3.5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs border border-slate-700 transition-all flex items-center gap-1.5 active:scale-95"
                      title="Print all bills / Save as PDF via Print dialog"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      <span>Print All Bills</span>
                    </button>

                    {/* Create Person Cloud Backup */}
                    <button
                      onClick={() => handleCreatePersonCloudBackup(selectedPerson)}
                      className="px-3.5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs transition-all flex items-center gap-1.5 active:scale-95"
                      title="Save a dedicated cloud backup snapshot for this person"
                    >
                      <span>☁️</span>
                      <span>Backup to Cloud</span>
                    </button>
                  </div>
                </div>

                {/* SEPARATE BILLS LIST FOR THIS SPECIFIC PERSON */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between px-1">
                    <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                      <span>🧾</span>
                      <span>Separate Bills for {selectedPerson.name} ({selectedPerson.sales.length} Separate Invoices)</span>
                    </h3>
                    <span className="text-xs text-slate-500 font-semibold">
                      Each bill can be viewed, printed, or downloaded separately
                    </span>
                  </div>

                  {selectedPerson.sales.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
                      <p className="text-sm font-bold text-slate-500">No sales bills found for {selectedPerson.name}.</p>
                      {selectedPerson.credit && (
                        <p className="text-xs text-rose-600 font-bold mt-1">This customer has a recorded credit / udhari account of ₹{parseFloat(selectedPerson.credit.dueAmount || 0).toFixed(2)}.</p>
                      )}
                    </div>
                  ) : (
                    selectedPerson.sales.map((bill, index) => {
                      const isExpanded = personExpandedBillId === (bill.id || index);
                      const netAmt = parseFloat(bill.netAmount || bill.grossAmount || 0);
                      const items = bill.items || [];

                      return (
                        <div key={bill.id || index} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all overflow-hidden">
                          {/* Separate Bill Header */}
                          <div className="p-4 sm:p-5 bg-gradient-to-r from-slate-50 via-white to-slate-50 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3.5">
                              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center font-black text-sm shrink-0 border border-blue-100">
                                #{index + 1}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-black text-slate-900">
                                    Invoice #{bill.invoiceNo || bill.vchNo || ('BILL-' + (index + 1))}
                                  </h4>
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                    bill.saleType === 'CASH' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                    bill.saleType === 'ONLINE' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                    bill.saleType === 'CREDIT' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                    'bg-purple-50 text-purple-700 border border-purple-200'
                                  }`}>
                                    {bill.saleType || 'Cash'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-500 font-semibold mt-0.5">
                                  Date: <strong className="text-slate-700">{bill.date || '-'}</strong> {bill.time ? `• Time: ${bill.time}` : ''} • Items: <strong className="text-slate-700">{items.length}</strong>
                                </p>
                              </div>
                            </div>

                            {/* Bill Actions: View Receipt, Print, Download PDF */}
                            <div className="flex items-center justify-between sm:justify-end gap-3">
                              <div className="text-right mr-2">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bill Total</p>
                                <p className="text-base font-black text-slate-900">₹{netAmt.toFixed(2)}</p>
                              </div>

                              <div className="flex items-center gap-1.5">
                                {/* View Receipt */}
                                <button
                                  onClick={() => handleViewBillReceipt(bill)}
                                  className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs transition-all shadow-xs flex items-center gap-1 active:scale-95"
                                  title="View Authentic Medical Receipt"
                                >
                                  <span>🧾</span>
                                  <span>View Receipt</span>
                                </button>

                                {/* Print Separate Bill */}
                                <button
                                  onClick={() => handlePrintSingleBill(bill)}
                                  className="px-2.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs transition-all flex items-center gap-1 active:scale-95"
                                  title="Print separate bill / Save as PDF"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                  </svg>
                                  <span>Print</span>
                                </button>

                                {/* Download PDF Receipt */}
                                <button
                                  onClick={() => handleDownloadSingleBillPdf(bill, selectedPerson.name)}
                                  className="px-2.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs transition-all flex items-center gap-1 active:scale-95"
                                  title="Download single bill as PDF"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <span>PDF</span>
                                </button>

                                {/* Toggle Table Expansion */}
                                <button
                                  onClick={() => setPersonExpandedBillId(isExpanded ? null : (bill.id || index))}
                                  className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs transition-colors"
                                  title={isExpanded ? "Collapse Items" : "Expand Items"}
                                >
                                  {isExpanded ? '▲' : '▼'}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Items Table for This Bill */}
                          <div className={`p-4 bg-slate-50/60 ${isExpanded ? 'block' : 'hidden'}`}>
                            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                              <table className="w-full text-left text-xs border-collapse">
                                <thead>
                                  <tr className="bg-slate-100 text-slate-600 text-[10px] uppercase font-black border-b border-slate-200">
                                    <th className="p-2.5">#</th>
                                    <th className="p-2.5">Medicine / Description</th>
                                    <th className="p-2.5">Batch</th>
                                    <th className="p-2.5">Exp</th>
                                    <th className="p-2.5 text-center">Qty</th>
                                    <th className="p-2.5 text-right">Rate</th>
                                    <th className="p-2.5 text-right">MRP</th>
                                    <th className="p-2.5 text-right">Amount</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {items.length === 0 ? (
                                    <tr><td colSpan="8" className="p-3 text-center text-slate-400">No items listed in bill.</td></tr>
                                  ) : (
                                    items.map((it, idx) => (
                                      <tr key={idx} className="hover:bg-slate-50">
                                        <td className="p-2.5 text-slate-400">{idx + 1}</td>
                                        <td className="p-2.5 font-black text-slate-800 uppercase">{it.productName || '-'}</td>
                                        <td className="p-2.5 text-slate-600">{it.batch || '-'}</td>
                                        <td className="p-2.5 text-slate-600">{it.exp || '-'}</td>
                                        <td className="p-2.5 text-center font-bold text-blue-600">{it.qty || 1}</td>
                                        <td className="p-2.5 text-right text-slate-600">₹{parseFloat(it.rate || 0).toFixed(2)}</td>
                                        <td className="p-2.5 text-right text-slate-400">₹{parseFloat(it.mrp || 0).toFixed(2)}</td>
                                        <td className="p-2.5 text-right font-black text-slate-900">
                                          ₹{parseFloat(it.amount || ((parseFloat(it.qty) || 1) * (parseFloat(it.rate) || 0))).toFixed(2)}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              /* No Person Selected Placeholder */
              <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/80 shadow-sm space-y-3">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-3xl mx-auto">
                  👤
                </div>
                <h3 className="text-base font-black text-slate-900">Select a Customer to View Their Separate Bills</h3>
                <p className="text-xs text-slate-500 max-w-md mx-auto">
                  Click on any customer name above or search by name to display all individual bills, print authentic receipts, and export customer backup.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ======================================================== */}
        {/* VIEW 2: FULL SYSTEM BACKUP SNAPSHOTS TABLE              */}
        {/* ======================================================== */}
        {activeMainTab === 'system' && (
          <div className="space-y-4">
            {/* Analytics Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between group">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Backups</p>
                  <h3 className="text-xl font-black text-slate-900 mt-0.5">{backups.length}</h3>
                  <p className="text-[10px] text-emerald-600 font-bold">Cloud Snapshots</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-base group-hover:scale-110 transition-transform">
                  🗄️
                </div>
              </div>

              <div className="bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between group">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Last Backup</p>
                  <h3 className="text-xs font-black text-slate-900 mt-0.5 truncate max-w-[120px]">
                    {backups[0] ? backups[0].date : 'None'}
                  </h3>
                  <p className="text-[10px] text-slate-400 font-bold">{backups[0]?.time || 'Awaiting backup'}</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-base group-hover:scale-110 transition-transform">
                  ⏱️
                </div>
              </div>

              <div className="bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between group">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Format</p>
                  <h3 className="text-xs font-black text-emerald-600 mt-0.5">
                    Receipt PDF
                  </h3>
                  <p className="text-[10px] text-slate-400 font-bold">Authentic Medical Bill</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold text-base group-hover:scale-110 transition-transform">
                  📄
                </div>
              </div>

              <div className="bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between group">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Multi-PC Sync</p>
                  <h3 className="text-xs font-black text-emerald-600 mt-0.5">Realtime Active</h3>
                  <p className="text-[10px] text-slate-400 font-bold">Cloud Connected</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center font-bold text-base group-hover:scale-110 transition-transform">
                  ☁️
                </div>
              </div>
            </div>

            {/* Backups List Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden flex flex-col">
              {/* Table Header / Toolbar */}
              <div className="p-3.5 sm:p-4 border-b border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-slate-50/50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center font-bold text-xs shadow-sm">
                    📋
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-900 tracking-tight">
                      System Backup Snapshots
                    </h3>
                    <p className="text-[10px] text-slate-500 font-medium">
                      One-click authentic Medical Store Receipt PDF downloads & direct print
                    </p>
                  </div>
                </div>

                {/* Search Input */}
                <div className="relative w-full sm:w-72">
                  <input
                    type="text"
                    placeholder="Search by date, note, or PC..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm transition-all"
                  />
                  <svg className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Table Content */}
              {loading ? (
                <div className="py-16 flex flex-col items-center justify-center text-center">
                  <div className="w-9 h-9 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-xs font-bold text-slate-500">Loading cloud backup snapshots...</p>
                </div>
              ) : filteredBackups.length === 0 ? (
                <div className="py-16 flex flex-col items-center justify-center text-center px-4">
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-2xl mb-3 text-slate-400">
                    📦
                  </div>
                  <h4 className="text-sm font-black text-slate-800">No Backup Snapshots Found</h4>
                  <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                    Click the <strong className="text-emerald-600">"Create Cloud Backup"</strong> button to save your first snapshot in authentic Receipt PDF format.
                  </p>
                  <button
                    onClick={() => {
                      setBackupType('full');
                      setSelectedPersonForBackup(null);
                      setShowNewBackupModal(true);
                    }}
                    className="mt-3.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow transition-all active:scale-95"
                  >
                    + Create Cloud Backup
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50/80 text-slate-500 uppercase text-[9px] font-black border-b border-slate-200/80 tracking-wider">
                        <th className="py-3 px-3.5">Backup Name & Date</th>
                        <th className="py-3 px-3">Time</th>
                        <th className="py-3 px-3">Terminal / PC</th>
                        <th className="py-3 px-3">Notes</th>
                        <th className="py-3 px-3 text-center">Data Summary</th>
                        <th className="py-3 px-3.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredBackups.map((b) => (
                        <tr key={b.id} className="hover:bg-slate-50/80 transition-colors group">
                          <td className="py-2.5 px-3.5 font-bold text-slate-800">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-500/20 flex items-center justify-center font-bold text-xs shrink-0 group-hover:scale-105 transition-transform">
                                📁
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="text-xs font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">{b.name}</p>
                                  <span className="px-1.5 py-0.2 rounded text-[8px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    PDF Ready
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-400 font-medium">{b.date}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 px-3 font-semibold text-slate-600 whitespace-nowrap text-[11px]">
                            {b.time || '-'}
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-slate-100 text-slate-700 text-[10px] font-bold border border-slate-200 max-w-[180px] truncate" title={b.deviceInfo || 'PC Terminal'}>
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
                              <span className="truncate">{b.deviceInfo || 'PC Terminal'}</span>
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-slate-600 font-medium max-w-[140px] truncate text-[11px]" title={b.notes}>
                            {b.notes || 'Full System Snapshot'}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <div className="inline-flex items-center gap-1.5">
                              <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-bold text-[9px] border border-blue-100/80">
                                🛒 {b.stats?.totalSales || 0} Bills
                              </span>
                              <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 font-bold text-[9px] border border-purple-100/80">
                                📥 {b.stats?.totalPurchases || 0} Inward
                              </span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              {/* View Bills Button */}
                              <button
                                onClick={() => handleOpenBillViewer(b)}
                                className="px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-[11px] transition-all shadow-xs flex items-center gap-1 hover:scale-105 active:scale-95"
                                title="View all bills and invoices in this backup"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                                <span>View</span>
                              </button>

                              {/* Download Receipt PDF Button */}
                              <button
                                onClick={() => handleDownloadReceiptPdf(b.id, b.name)}
                                className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] transition-all flex items-center gap-1 shadow-xs hover:scale-105 active:scale-95"
                                title="Download authentic Medical Receipt PDF"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                                <span>Receipt PDF</span>
                              </button>

                              {/* Print Receipt Button */}
                              <button
                                onClick={() => handlePrintBackupReceipt(b.id)}
                                className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold text-[11px] transition-all flex items-center gap-1 shadow-xs active:scale-95"
                                title="Print receipt / Save as PDF"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                                <span>Print</span>
                              </button>

                              {/* Restore Button */}
                              <button
                                onClick={() => setSelectedBackupForRestore(b)}
                                className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 font-bold text-[11px] transition-colors flex items-center gap-1 shadow-xs active:scale-95"
                                title="Restore this backup to database"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                                <span>Restore</span>
                              </button>

                              {/* Secondary Raw JSON download */}
                              <button
                                onClick={() => handleDownloadCompactJson(b.id, b.name)}
                                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                title="Download raw JSON data (for database restore)"
                              >
                                <span className="text-[10px] font-black">JSON</span>
                              </button>

                              {/* Delete Button */}
                              <button
                                onClick={() => handleDeleteBackup(b.id)}
                                className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                                title="Delete backup snapshot"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* MODAL: FULL-SCREEN BILL EXPLORER (With Customer Filter)   */}
        {/* ======================================================== */}
        {viewingBackupData && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-[250] flex items-center justify-center p-2 sm:p-4 md:p-6 animate-in fade-in duration-200">
            <div className="bg-slate-50 w-full max-w-6xl h-[92vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden border border-slate-700/50 animate-in zoom-in-95 duration-200">
              
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-[#0F172A] via-[#1E293B] to-[#0A0F1C] text-white p-5 sm:p-6 border-b border-slate-800 shrink-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2.5">
                    <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase tracking-wider">
                      Backup Invoice Explorer
                    </span>
                    <span className="text-xs text-slate-400 font-medium">
                      📅 {viewingBackupData.date} • 🕒 {viewingBackupData.time}
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-white mt-1 tracking-tight">
                    {viewingBackupData.name}
                  </h2>
                  <p className="text-xs text-slate-400 font-medium">
                    Origin Terminal: <strong className="text-slate-200">{viewingBackupData.deviceInfo}</strong> • Note: {viewingBackupData.notes || 'Full System Snapshot'}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleDownloadReceiptPdf(viewingBackupData.id, viewingBackupData.name)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
                    title="Download complete register as authentic receipt PDF"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                    Download Receipt PDF
                  </button>

                  <button
                    onClick={() => handlePrintBackupReceipt(viewingBackupData.id)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold transition-all border border-slate-700 flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                    Print
                  </button>

                  <button
                    onClick={() => setViewingBackupData(null)}
                    className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Navigation Tabs & Search Toolbar */}
              <div className="bg-white border-b border-slate-200 px-5 py-3.5 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 shrink-0 shadow-sm">
                <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
                  <button
                    onClick={() => { setBillViewTab('sales'); setBillSearchQuery(''); }}
                    className={`px-4 py-2 rounded-xl font-black text-xs transition-all flex items-center gap-2 shrink-0 ${
                      billViewTab === 'sales'
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    <span>🛒 Sales Invoices</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                      billViewTab === 'sales' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                    }`}>
                      {viewingBackupData.data?.sales?.length || 0}
                    </span>
                  </button>

                  <button
                    onClick={() => { setBillViewTab('purchases'); setBillSearchQuery(''); }}
                    className={`px-4 py-2 rounded-xl font-black text-xs transition-all flex items-center gap-2 shrink-0 ${
                      billViewTab === 'purchases'
                        ? 'bg-purple-600 text-white shadow-md shadow-purple-500/25'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    <span>📥 Purchase Bills</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                      billViewTab === 'purchases' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                    }`}>
                      {viewingBackupData.data?.purchases?.length || 0}
                    </span>
                  </button>

                  <button
                    onClick={() => { setBillViewTab('credit'); setBillSearchQuery(''); }}
                    className={`px-4 py-2 rounded-xl font-black text-xs transition-all flex items-center gap-2 shrink-0 ${
                      billViewTab === 'credit'
                        ? 'bg-rose-600 text-white shadow-md shadow-rose-500/25'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    <span>💳 Credit Ledgers</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                      billViewTab === 'credit' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                    }`}>
                      {viewingBackupData.data?.credit?.length || 0}
                    </span>
                  </button>

                  <button
                    onClick={() => { setBillViewTab('stockists'); setBillSearchQuery(''); }}
                    className={`px-4 py-2 rounded-xl font-black text-xs transition-all flex items-center gap-2 shrink-0 ${
                      billViewTab === 'stockists'
                        ? 'bg-teal-600 text-white shadow-md shadow-teal-500/25'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    <span>🏢 Stockists Directory</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                      billViewTab === 'stockists' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                    }`}>
                      {viewingBackupData.data?.stockist?.length || 0}
                    </span>
                  </button>
                </div>

                <div className="flex items-center gap-2.5">
                  {/* Customer Filter Dropdown inside Sales */}
                  {billViewTab === 'sales' && modalCustomersList.length > 0 && (
                    <select
                      value={modalCustomerFilter}
                      onChange={(e) => setModalCustomerFilter(e.target.value)}
                      className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Customers ({modalCustomersList.length})</option>
                      {modalCustomersList.map((c, i) => (
                        <option key={i} value={c}>👤 {c}</option>
                      ))}
                    </select>
                  )}

                  {/* Filter Search */}
                  <div className="relative w-full md:w-64">
                    <input
                      type="text"
                      placeholder="Search in these bills..."
                      value={billSearchQuery}
                      onChange={(e) => setBillSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <svg className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Bills Content Area */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
                
                {/* 1. SALES BILLS VIEW */}
                {billViewTab === 'sales' && (
                  currentBillsList.length === 0 ? (
                    <div className="py-20 text-center text-slate-400 font-bold text-sm">
                      No sales bills found in this backup snapshot.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {currentBillsList.map((bill, index) => {
                        const isExpanded = expandedBillId === (bill.id || index);
                        const netAmt = parseFloat(bill.netAmount || bill.grossAmount || 0);
                        const items = bill.items || [];

                        return (
                          <div key={bill.id || index} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden hover:shadow-md transition-all">
                            {/* Bill Header */}
                            <div className="p-4 sm:p-5 bg-gradient-to-r from-slate-50 via-white to-slate-50 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="flex items-center gap-3.5">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-black text-sm shrink-0 border border-blue-100">
                                  #{index + 1}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-black text-slate-900 uppercase">
                                      {bill.custName || 'Walk-in Customer'}
                                    </h4>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                      bill.saleType === 'CASH' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                      bill.saleType === 'ONLINE' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                      bill.saleType === 'CREDIT' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                      'bg-purple-50 text-purple-700 border border-purple-200'
                                    }`}>
                                      {bill.saleType || 'Cash'}
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-slate-500 font-semibold mt-0.5">
                                    Inv: <strong className="text-slate-700">{bill.invoiceNo || bill.vchNo || '-'}</strong> • Date: <strong className="text-slate-700">{bill.date || '-'}</strong> {bill.custMob ? `• 📞 ${bill.custMob}` : ''}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center justify-between sm:justify-end gap-3">
                                <div className="text-right mr-2">
                                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bill Total</p>
                                  <p className="text-lg font-black text-slate-900">₹{netAmt.toFixed(2)}</p>
                                </div>

                                {/* Bill Action Buttons */}
                                <button
                                  onClick={() => handleViewBillReceipt(bill)}
                                  className="px-2.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center gap-1 shadow-xs"
                                  title="View Receipt"
                                >
                                  <span>🧾 Receipt</span>
                                </button>
                                <button
                                  onClick={() => handlePrintSingleBill(bill)}
                                  className="px-2.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center gap-1 shadow-xs"
                                  title="Print Bill"
                                >
                                  <span>🖨️ Print</span>
                                </button>
                                <button
                                  onClick={() => handleDownloadSingleBillPdf(bill, bill.custName)}
                                  className="px-2.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1 shadow-xs"
                                  title="Download PDF"
                                >
                                  <span>📄 PDF</span>
                                </button>

                                <button 
                                  onClick={() => setExpandedBillId(isExpanded ? null : (bill.id || index))}
                                  className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm"
                                >
                                  {isExpanded ? '▲' : '▼'}
                                </button>
                              </div>
                            </div>

                            {/* Detailed Bill Items Table */}
                            <div className={`p-4 bg-slate-50/50 ${isExpanded ? 'block' : 'hidden'}`}>
                              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                <table className="w-full text-left text-xs border-collapse">
                                  <thead>
                                    <tr className="bg-slate-100 text-slate-600 text-[10px] uppercase font-black border-b border-slate-200">
                                      <th className="p-2.5">Medicine / Item</th>
                                      <th className="p-2.5">Batch</th>
                                      <th className="p-2.5">Exp</th>
                                      <th className="p-2.5 text-center">Qty</th>
                                      <th className="p-2.5 text-right">Rate</th>
                                      <th className="p-2.5 text-right">MRP</th>
                                      <th className="p-2.5 text-right">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {items.length === 0 ? (
                                      <tr><td colSpan="7" className="p-3 text-center text-slate-400">No items listed in bill.</td></tr>
                                    ) : (
                                      items.map((it, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50">
                                          <td className="p-2.5 font-black text-slate-800 uppercase">{it.productName || '-'}</td>
                                          <td className="p-2.5 font-semibold text-slate-600">{it.batch || '-'}</td>
                                          <td className="p-2.5 text-slate-600">{it.exp || '-'}</td>
                                          <td className="p-2.5 text-center font-bold text-blue-600">{it.qty || 1}</td>
                                          <td className="p-2.5 text-right text-slate-600">₹{parseFloat(it.rate || 0).toFixed(2)}</td>
                                          <td className="p-2.5 text-right text-slate-400">₹{parseFloat(it.mrp || 0).toFixed(2)}</td>
                                          <td className="p-2.5 text-right font-black text-slate-900">
                                            ₹{parseFloat(it.amount || ((parseFloat(it.qty) || 1) * (parseFloat(it.rate) || 0))).toFixed(2)}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

                {/* 2. PURCHASE INWARDS VIEW */}
                {billViewTab === 'purchases' && (
                  currentBillsList.length === 0 ? (
                    <div className="py-20 text-center text-slate-400 font-bold text-sm">
                      No purchase bills found in this backup snapshot.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {currentBillsList.map((pur, index) => {
                        const isExpanded = expandedBillId === (pur.id || index);
                        const netAmt = parseFloat(pur.netAmount || pur.grossAmount || pur.totalAmount || 0);
                        const items = pur.items || [];

                        return (
                          <div key={pur.id || index} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden hover:shadow-md transition-all">
                            {/* Purchase Header */}
                            <div 
                              onClick={() => setExpandedBillId(isExpanded ? null : (pur.id || index))}
                              className="p-4 sm:p-5 bg-gradient-to-r from-purple-50/50 via-white to-purple-50/50 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer select-none"
                            >
                              <div className="flex items-center gap-3.5">
                                <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-black text-sm shrink-0 border border-purple-100">
                                  #{index + 1}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-black text-slate-900 uppercase">
                                      {pur.supplierName || 'Distributor / Stockist'}
                                    </h4>
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-purple-50 text-purple-700 border border-purple-200">
                                      {pur.paymentType || 'Credit'}
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-slate-500 font-semibold mt-0.5">
                                    Inv #: <strong className="text-slate-700">{pur.invoiceNo || '-'}</strong> • Date: <strong className="text-slate-700">{pur.date || '-'}</strong> {pur.supplierGst ? `• GST: ${pur.supplierGst}` : ''}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center justify-between sm:justify-end gap-4">
                                <div className="text-right">
                                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Inward Amount</p>
                                  <p className="text-lg font-black text-purple-700">₹{netAmt.toFixed(2)}</p>
                                </div>
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">
                                  {isExpanded ? '▲' : '▼'}
                                </div>
                              </div>
                            </div>

                            {/* Purchase Items Table */}
                            <div className={`p-4 bg-slate-50/50 ${isExpanded ? 'block' : 'hidden'}`}>
                              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                <table className="w-full text-left text-xs border-collapse">
                                  <thead>
                                    <tr className="bg-slate-100 text-slate-600 text-[10px] uppercase font-black border-b border-slate-200">
                                      <th className="p-2.5">Medicine Name</th>
                                      <th className="p-2.5">Batch</th>
                                      <th className="p-2.5">Exp</th>
                                      <th className="p-2.5 text-center">Qty</th>
                                      <th className="p-2.5 text-right">Purchase Rate</th>
                                      <th className="p-2.5 text-right">MRP</th>
                                      <th className="p-2.5 text-right">Net Value</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {items.length === 0 ? (
                                      <tr><td colSpan="7" className="p-3 text-center text-slate-400">No items listed.</td></tr>
                                    ) : (
                                      items.map((it, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50">
                                          <td className="p-2.5 font-black text-slate-800 uppercase">{it.productName || '-'}</td>
                                          <td className="p-2.5 font-semibold text-slate-600">{it.batch || '-'}</td>
                                          <td className="p-2.5 text-slate-600">{it.exp || '-'}</td>
                                          <td className="p-2.5 text-center font-bold text-purple-600">{it.qty || 1}</td>
                                          <td className="p-2.5 text-right text-slate-700 font-bold">₹{parseFloat(it.rate || 0).toFixed(2)}</td>
                                          <td className="p-2.5 text-right text-slate-400">₹{parseFloat(it.mrp || 0).toFixed(2)}</td>
                                          <td className="p-2.5 text-right font-black text-slate-900">
                                            ₹{parseFloat(it.amount || ((parseFloat(it.qty) || 1) * (parseFloat(it.rate) || 0))).toFixed(2)}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

                {/* 3. CREDIT ACCOUNTS VIEW */}
                {billViewTab === 'credit' && (
                  currentBillsList.length === 0 ? (
                    <div className="py-20 text-center text-slate-400 font-bold text-sm">
                      No credit / udhari accounts recorded in this backup.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {currentBillsList.map((c, index) => (
                        <div key={c.id || index} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
                          <div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-black text-slate-400">AC #{index + 1}</span>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-200">
                                DUE
                              </span>
                            </div>
                            <h4 className="text-base font-black text-slate-900 mt-2 uppercase">{c.name || 'Customer'}</h4>
                            <p className="text-xs text-slate-500 font-medium">📞 {c.phone || 'No phone'}</p>
                            <p className="text-[11px] text-slate-400 mt-1">Last Updated: {c.lastUpdate || '-'}</p>
                          </div>
                          <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                            <span className="text-xs font-bold text-slate-500">Total Due Amount</span>
                            <span className="text-base font-black text-rose-600">₹{parseFloat(c.dueAmount || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}

                {/* 4. STOCKISTS DIRECTORY VIEW */}
                {billViewTab === 'stockists' && (
                  currentBillsList.length === 0 ? (
                    <div className="py-20 text-center text-slate-400 font-bold text-sm">
                      No stockist records found in this backup.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {currentBillsList.map((s, index) => (
                        <div key={s.id || index} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
                          <div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-black text-slate-400">Supplier #{index + 1}</span>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-teal-50 text-teal-700 border border-teal-200">
                                {s.totalBills || 0} Invoices
                              </span>
                            </div>
                            <h4 className="text-base font-black text-slate-900 mt-2 uppercase">{s.name}</h4>
                            <p className="text-xs text-slate-500 font-medium">📍 {s.address || '-'}</p>
                            <p className="text-xs text-slate-500 font-medium">📞 {s.phone || '-'}</p>
                            {s.gst && <p className="text-[11px] text-slate-400 mt-1">GST: {s.gst}</p>}
                          </div>
                          <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                            <span className="text-xs font-bold text-slate-500">Total Purchase Value</span>
                            <span className="text-base font-black text-teal-700">₹{parseFloat(s.totalPurchases || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}

              </div>

              {/* Modal Footer */}
              <div className="p-4 bg-white border-t border-slate-200 flex justify-between items-center shrink-0">
                <div className="text-xs font-bold text-slate-500">
                  Showing structured bill register for {viewingBackupData.name}
                </div>
                <button
                  onClick={() => setViewingBackupData(null)}
                  className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-xs transition-colors shadow-sm"
                >
                  Close Explorer
                </button>
              </div>

            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* MODAL: IN-APP RECEIPT PREVIEW MODAL                      */}
        {/* ======================================================== */}
        {previewReceiptHtml && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-[300] flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-4xl h-[92vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="bg-slate-900 text-white p-4 px-6 flex items-center justify-between border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <span className="text-base">🧾</span>
                  <h3 className="text-sm font-black text-white">{previewReceiptTitle || 'Medical Store Receipt'}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => printReceiptDirectly(previewReceiptHtml)}
                    className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs transition-all flex items-center gap-1 shadow-sm"
                  >
                    <span>🖨️ Print / Save as PDF</span>
                  </button>
                  <button
                    onClick={() => downloadReceiptPdf(previewReceiptHtml, 'Pharmacy_Bill_Receipt.pdf')}
                    className="px-3.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs transition-all flex items-center gap-1 shadow-sm"
                  >
                    <span>📄 Download PDF</span>
                  </button>
                  <button
                    onClick={() => setPreviewReceiptHtml(null)}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-base transition-colors ml-2"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Rendered HTML inside iframe for exact CSS isolation */}
              <div className="flex-1 overflow-hidden bg-slate-100">
                <iframe
                  title="Receipt Preview"
                  srcDoc={previewReceiptHtml}
                  className="w-full h-full border-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* MODAL: CREATE CLOUD BACKUP (Full or Specific Person)    */}
        {/* ======================================================== */}
        {showNewBackupModal && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200">
              <div className="p-6 bg-gradient-to-r from-slate-900 via-slate-800 to-[#0A0F1C] text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-black tracking-tight text-white">Create Cloud Backup</h3>
                    <p className="text-xs text-slate-300 mt-0.5">Captures snapshot and downloads authentic Medical Receipt PDF</p>
                  </div>
                  <button 
                    onClick={() => setShowNewBackupModal(false)}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-base transition-colors"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Backup Scope Toggle: Full or Person */}
                <div>
                  <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-2">
                    Backup Target (बॅकअप प्रकार)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setBackupType('full')}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        backupType === 'full'
                          ? 'border-emerald-600 bg-emerald-50/50 text-slate-900 ring-2 ring-emerald-500/20'
                          : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                      }`}
                    >
                      <div className="font-black text-xs flex items-center gap-1.5">
                        <span>🗄️</span>
                        <span>Full System Backup</span>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">All sales, purchases & accounts</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setBackupType('person')}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        backupType === 'person'
                          ? 'border-blue-600 bg-blue-50/50 text-slate-900 ring-2 ring-blue-500/20'
                          : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                      }`}
                    >
                      <div className="font-black text-xs flex items-center gap-1.5">
                        <span>👤</span>
                        <span>Specific Person Backup</span>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">Single customer separate bills</p>
                    </button>
                  </div>
                </div>

                {/* If Person Backup, Dropdown to choose customer */}
                {backupType === 'person' && (
                  <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl space-y-2">
                    <label className="block text-xs font-black text-blue-900 uppercase tracking-wider">
                      Select Customer / Person (ग्राहक निवडा):
                    </label>
                    <select
                      value={selectedPersonForBackup?.name || ''}
                      onChange={(e) => {
                        const found = aggregatedPersons.find(p => p.name === e.target.value);
                        setSelectedPersonForBackup(found || null);
                      }}
                      className="w-full px-3 py-2 bg-white border border-blue-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Choose a customer --</option>
                      {aggregatedPersons.map((p, i) => (
                        <option key={i} value={p.name}>
                          {p.name} ({p.sales.length} Bills • ₹{p.totalSpent.toFixed(0)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-1.5">
                    Backup Label / Note (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Closing Snapshot or Monthly Customer Statement"
                    value={backupNotes}
                    onChange={(e) => setBackupNotes(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>

                <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-100 text-xs text-slate-700 space-y-1.5">
                  <div className="font-black text-emerald-900 flex items-center gap-1.5">
                    <span>📄</span> Format: Authentic Pharmacy Receipt PDF
                  </div>
                  <p className="text-[11px] text-slate-600">
                    The backup will automatically generate and download in receipt PDF format (no raw JSON), styled exactly like a pharmacy tax invoice.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowNewBackupModal(false)}
                    className="flex-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateBackup}
                    disabled={isCreatingBackup || (backupType === 'person' && !selectedPersonForBackup)}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black text-xs transition-colors shadow-md shadow-emerald-500/25 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isCreatingBackup ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                        <span>Saving & Generating PDF...</span>
                      </>
                    ) : (
                      <span>Save & Download PDF</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* MODAL: CONFIRM RESTORE                                   */}
        {/* ======================================================== */}
        {selectedBackupForRestore && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200">
              <div className="p-6 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-black tracking-tight">Restore Database Snapshot</h3>
                    <p className="text-xs text-amber-100 mt-0.5">{selectedBackupForRestore.name}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedBackupForRestore(null)}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-base transition-colors"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-900 leading-relaxed">
                  <strong className="block font-black mb-1">⚠️ Confirmation Required:</strong>
                  Restoring will update and merge the records from this backup snapshot into your active database. Are you sure you want to proceed?
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl space-y-1.5 text-xs border border-slate-200/70">
                  <p className="font-bold text-slate-700">Timestamp: <span className="font-normal text-slate-900">{selectedBackupForRestore.date} {selectedBackupForRestore.time}</span></p>
                  <p className="font-bold text-slate-700">Origin PC: <span className="font-normal text-slate-900">{selectedBackupForRestore.deviceInfo}</span></p>
                  <p className="font-bold text-slate-700">Note: <span className="font-normal text-slate-900">{selectedBackupForRestore.notes || 'Full System Backup'}</span></p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setSelectedBackupForRestore(null)}
                    className="flex-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmRestore}
                    disabled={isRestoring}
                    className="flex-1 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-black text-xs transition-colors shadow-md shadow-amber-500/25 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isRestoring ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                        <span>Restoring...</span>
                      </>
                    ) : (
                      <span>Confirm Restore</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Data Migration from Old Software (Excel/CSV) */}
        <DataMigrationModal
          isOpen={showMigrationModal}
          onClose={() => setShowMigrationModal(false)}
          onImportSuccess={() => {
            fetchAllData();
            setShowMigrationModal(false);
          }}
        />

      </div>
    </div>
  );
};

export default Backups;
